import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Paper, Typography, makeStyles, Button, Icon } from "@material-ui/core";
import { WarningRounded } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import moment from "moment";

import { Site, MaintenanceModeStatusDto } from "../../dtos/site";
import { useApi } from "../useApi";
import { AjaxResult } from "../../enums/ajaxResult";
import SignalRContext from "../../signalr/context";
import { EnableMaintenanceModeDialog } from "../pages/sites/EnableMaintenanceModeDialog";

const useStyles = makeStyles(() => ({
    container: {
        alignSelf: "stretch",
        padding: "12px 16px",
        minWidth: "400px",
        display: "flex",
        gap: "12px",
        alignItems: "center",
        backgroundColor: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(245, 158, 11, 0.12)",
    },
    content: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    message: {
        margin: 0,
        fontSize: "14px",
        fontWeight: 500,
        color: "#92400e",
    },
    timeRemaining: {
        margin: 0,
        fontSize: "0.875rem",
        color: "#92400e",
        opacity: 0.75,
    },
    comment: {
        margin: 0,
        fontSize: "0.875rem",
        color: "#92400e",
        opacity: 0.75,
        wordBreak: "break-word",
    },
    actions: {
        display: "flex",
        gap: "8px",
        flexShrink: 0,
    },
}));

interface Props {
    site: Site | undefined;
}

export function SiteMaintenanceModeBanner({ site }: Props) {
    const classes = useStyles();
    const { maintenanceModeApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { maintenanceModeStatus } = useContext(SignalRContext);
    const [status, setStatus] = useState<MaintenanceModeStatusDto | null>(null);
    const [timeRemaining, setTimeRemaining] = useState("");
    const [loading, setLoading] = useState(false);
    const [showExtendDialog, setShowExtendDialog] = useState(false);

    const loadStatus = useCallback((options?: { forceRefresh?: boolean }) => {
        if (!site?.id) return;

        maintenanceModeApi.getStatus(site.id, options).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setStatus(r.data);
            } else {
                setStatus(null);
            }
        });
    }, [maintenanceModeApi, site?.id]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Update status from SignalR
    const isFirstStatusEffect = useRef(true);
    useEffect(() => {
        if (isFirstStatusEffect.current) {
            isFirstStatusEffect.current = false;
            return;
        }

        if (!site?.id) return;

        // Get the SignalR status for this site
        const signalRStatus = maintenanceModeStatus.get(site.id);

        // Refresh status when SignalR indicates a change for this site
        if (signalRStatus !== undefined) {
            loadStatus({ forceRefresh: true });
        }
    }, [maintenanceModeStatus, site?.id, loadStatus]);

    // Update time remaining every second
    useEffect(() => {
        if (!status || !status.isInMaintenanceMode || !status.maintenanceMode) {
            return;
        }

        const updateTimeRemaining = () => {
            // Parse endTime as UTC, then convert to local time for comparison
            const end = moment.utc(status.maintenanceMode!.endTime).local();
            const now = moment();
            const duration = moment.duration(end.diff(now));

            if (duration.asMilliseconds() <= 0) {
                setTimeRemaining("Ending soon...");
                // Refresh status in case it expired
                setTimeout(() => loadStatus({ forceRefresh: true }), 1000);
            } else {
                const hours = Math.floor(duration.asHours());
                const minutes = duration.minutes();
                const seconds = duration.seconds();

                if (hours > 0) {
                    setTimeRemaining(`${hours}h ${minutes}m ${seconds}s remaining`);
                } else if (minutes > 0) {
                    setTimeRemaining(`${minutes}m ${seconds}s remaining`);
                } else {
                    setTimeRemaining(`${seconds}s remaining`);
                }
            }
        };

        updateTimeRemaining();
        const interval = setInterval(updateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [status, loadStatus]);

    const handleDisable = useCallback(() => {
        if (!site?.id) return;
        if (!window.confirm("Are you sure you want to disable maintenance mode?")) {
            return;
        }

        setLoading(true);
        maintenanceModeApi.disable(site.id).then((r) => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar("Maintenance mode disabled successfully", { variant: "success" });
                setStatus(null);
            } else {
                const errorMsg = r.messages && r.messages.length > 0
                    ? r.messages.join(", ")
                    : "Failed to disable maintenance mode";
                enqueueSnackbar(errorMsg, { variant: "error" });
            }
            setLoading(false);
        });
    }, [maintenanceModeApi, site?.id, enqueueSnackbar]);

    const handleExtendSuccess = useCallback(() => {
        loadStatus();
    }, [loadStatus]);

    const currentEndTime = useMemo(
        () => status?.maintenanceMode ? moment.utc(status.maintenanceMode.endTime).toDate() : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [status?.maintenanceMode?.endTime]
    );

    if (!site?.id || !status || !status.isInMaintenanceMode || !status.maintenanceMode) {
        return null;
    }

    return (
        <>
            <Paper className={classes.container} elevation={0}>
                <Icon><WarningRounded /></Icon>
                <div className={classes.content}>
                    <Typography className={classes.message}>
                        Maintenance Mode Active - Email notifications are suppressed
                    </Typography>
                    <Typography className={classes.timeRemaining}>
                        {timeRemaining}
                    </Typography>
                    {status.maintenanceMode.comment && (
                        <Typography className={classes.comment}>
                            {status.maintenanceMode.comment}
                        </Typography>
                    )}
                </div>
                <div className={classes.actions}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowExtendDialog(true)}
                        disabled={loading}
                    >
                        Extend
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={handleDisable}
                        disabled={loading}
                    >
                        Disable
                    </Button>
                </div>
            </Paper>

            {showExtendDialog && status.maintenanceMode && site?.id && (
                <EnableMaintenanceModeDialog
                    open={showExtendDialog}
                    onClose={() => setShowExtendDialog(false)}
                    siteId={site.id}
                    onSuccess={handleExtendSuccess}
                    mode="extend"
                    currentEndTime={currentEndTime}
                />
            )}
        </>
    );
}
